






































































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line as LineChart } from "vue-chartjs/legacy";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface DayRecord {
  fullDate: string;
  name: string;
  date: string;
  month?: string;
  schedPpd: string | null;
  actualPpd: string | null;
  targetPpd: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

@Component({
  components: { LineChart },
})
export default class TrendAnalysisTab extends Vue {
  /** Days array from the parent (used as default when no local filter is set) */
  @Prop({ default: () => [] }) days!: DayRecord[];
  @Prop({ default: false }) isActive!: boolean;

  // ─── Local date filter state ─────────────────────────────────────────────

  localStartDate = "";
  localEndDate = "";
  startDateMenu = false;
  endDateMenu = false;
  loading = false;
  /** Incremented every time fetch completes — forces LineChart to recreate */
  chartKey = 0;

  // ─── Initialise local dates from the parent's range on first activation ──

  @Watch("isActive", { immediate: true })
  onIsActiveChange(active: boolean) {
    if (active && !this.localStartDate && this.days.length) {
      this.localStartDate = this.days[0].fullDate;
      this.localEndDate = this.days[this.days.length - 1].fullDate;
    }
  }

  // ─── Re-fetch when local dates change ───────────────────────────────────

  @Watch("localStartDate")
  @Watch("localEndDate")
  onLocalDateChange() {
    this.fetchLocalPPD();
  }

  async fetchLocalPPD() {
    if (!this.localStartDate || !this.localEndDate) return;
    this.loading = true;
    try {
      await this.$store.dispatch("schedule/getSchedulePPD", {
        start: this.localStartDate,
        end: this.localEndDate,
      });
      // Bump key so LineChart is fully destroyed and recreated with clean data
      this.chartKey++;
    } catch (e) {
      console.error("TrendAnalysisTab: failed to fetch PPD", e);
    } finally {
      this.loading = false;
    }
  }

  onStartClear() {
    this.localStartDate = "";
  }

  onEndClear() {
    this.localEndDate = "";
  }

  // ─── Effective days: use local date-range over store PPD ─────────────────

  /**
   * Raw PPD records from the store — same source the parent uses.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get storePPD(): any[] {
    return this.$store.state.schedule.schedulePPD || [];
  }

  /**
   * Build a DayRecord for every calendar day in the local date range,
   * mapping PPD values from the store. For past/today days missing values
   * default to 0 to keep lines continuous. For future days with no data,
   * null is stored so the line ends naturally at the last known point.
   */
  get effectiveDays(): DayRecord[] {
    // Before local dates are seeded return empty so hasData stays false
    // and the loading/empty states render correctly.
    if (!this.localStartDate || !this.localEndDate) return [];

    const today = moment().startOf("day");
    const result: DayRecord[] = [];
    const current = moment(this.localStartDate, "YYYY-MM-DD");
    const end = moment(this.localEndDate, "YYYY-MM-DD");

    while (current.isSameOrBefore(end, "day")) {
      const fullDate = current.format("YYYY-MM-DD");
      const isFuture = current.isAfter(today, "day");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ppd = this.storePPD.find((p: any) => {
        const dateVal = p.date || p.Date;
        return dateVal && dateVal.startsWith(fullDate);
      });

      const getRaw = (primary: string, fallback: string) =>
        ppd
          ? ppd[primary] != null
            ? ppd[primary]
            : ppd[fallback] != null
            ? ppd[fallback]
            : null
          : null;

      const schedPpdRaw = getRaw("ppd", "PPD");
      const actualPpdRaw = getRaw("actual_ppd", "ActualPPD");
      const targetPpdRaw = getRaw("target_ppd", "TargetPPD");

      /**
       * Convert raw API value to a display string.
       * - If raw has a valid numeric value: use it.
       * - If raw is null and the day is in the future: return null so the
       *   chart line ends rather than dropping to 0.
       * - If raw is null and the day is past/today: return "0.00" to keep
       *   the historical line continuous.
       * - Guards against NaN from non-numeric API responses.
       */
      const toDisplayVal = (raw: number | string | null): string | null => {
        if (raw != null) {
          const n = Number(raw);
          return Number.isFinite(n) ? n.toFixed(2) : isFuture ? null : "0.00";
        }
        return isFuture ? null : "0.00";
      };

      result.push({
        fullDate,
        isFuture,
        name: current.format("ddd").toUpperCase(),
        date: current.format("D"),
        schedPpd: toDisplayVal(schedPpdRaw),
        actualPpd: toDisplayVal(actualPpdRaw),
        targetPpd: toDisplayVal(targetPpdRaw),
      });

      current.add(1, "day");
    }
    return result;
  }

  // ─── Chart data ──────────────────────────────────────────────────────────

  get hasData(): boolean {
    return this.effectiveDays.length > 0;
  }

  get labels(): string[] {
    return this.effectiveDays.map(d =>
      moment(d.fullDate, "YYYY-MM-DD").format("MMM D"),
    );
  }

  /** Safely parse a display string to a finite number or null. */
  private safeNum(val: string | null | undefined): number | null {
    if (val == null) return null;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : null;
  }

  get actualDataset(): (number | null)[] {
    return this.effectiveDays.map(d => this.safeNum(d.actualPpd));
  }

  get schedDataset(): (number | null)[] {
    return this.effectiveDays.map(d => this.safeNum(d.schedPpd));
  }

  get targetDataset(): (number | null)[] {
    return this.effectiveDays.map(d => this.safeNum(d.targetPpd));
  }

  get chartData() {
    return {
      labels: this.labels,
      datasets: [
        {
          label: "Actual PPD",
          data: this.actualDataset,
          borderColor: "#1565C0",
          backgroundColor: "rgba(21, 101, 192, 0.08)",
          borderWidth: 2.5,
          pointBackgroundColor: "#1565C0",
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: true,
          spanGaps: false,
        },
        {
          label: "Scheduled PPD",
          data: this.schedDataset,
          borderColor: "#2E7D32",
          backgroundColor: "rgba(46, 125, 50, 0.07)",
          borderWidth: 2.5,
          pointBackgroundColor: "#2E7D32",
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: true,
          spanGaps: false,
        },
        {
          label: "Target PPD",
          data: this.targetDataset,
          borderColor: "#E65100",
          backgroundColor: "rgba(230, 81, 0, 0.05)",
          borderWidth: 2,
          borderDash: [6, 4],
          pointBackgroundColor: "#E65100",
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.3,
          fill: false,
          spanGaps: false,
        },
      ],
    };
  }

  get chartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 600,
        easing: "easeInOutQuart" as const,
      },
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(255,255,255,0.97)",
          titleColor: "#1a237e",
          bodyColor: "#424242",
          borderColor: "#e0e0e0",
          borderWidth: 1,
          padding: 12,
          boxPadding: 4,
          callbacks: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label(ctx: any) {
              const val = ctx.parsed.y;
              if (val == null) return `${ctx.dataset.label}: 0.00`;
              return `${ctx.dataset.label}: ${val.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: "rgba(0,0,0,0.04)",
            drawBorder: false,
          },
          ticks: {
            color: "#616161",
            font: { size: 11 },
            maxRotation: 45,
            minRotation: 0,
            maxTicksLimit: 15,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(0,0,0,0.05)",
            drawBorder: false,
          },
          ticks: {
            color: "#616161",
            font: { size: 11 },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            callback(val: any) {
              return Number(val).toFixed(2);
            },
          },
          title: {
            display: true,
            text: "PPD",
            color: "#757575",
            font: { size: 11 },
          },
        },
      },
    };
  }

  // ─── Summary stats ───────────────────────────────────────────────────────

  private avgOf(values: (number | null)[]): string {
    const valid = values.filter(
      (v): v is number => v != null && !isNaN(v) && v > 0,
    );
    if (!valid.length) return "—";
    return (valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(2);
  }

  get mtdAvgActual(): string {
    return this.avgOf(this.actualDataset);
  }

  get mtdAvgSched(): string {
    return this.avgOf(this.schedDataset);
  }

  get mtdAvgTarget(): string {
    return this.avgOf(this.targetDataset);
  }

  get overBudgetDays(): number {
    return this.effectiveDays.filter(d => {
      const actual = d.actualPpd != null ? parseFloat(d.actualPpd) : 0;
      const target = d.targetPpd != null ? parseFloat(d.targetPpd) : 0;
      return actual >= target + 0.1 && target > 0;
    }).length;
  }

  // ─── Date range label ────────────────────────────────────────────────────

  get dateRangeLabel(): string {
    if (!this.effectiveDays.length) return "selected period";
    const first = moment(this.effectiveDays[0].fullDate, "YYYY-MM-DD").format(
      "MMM D",
    );
    const last = moment(
      this.effectiveDays[this.effectiveDays.length - 1].fullDate,
      "YYYY-MM-DD",
    ).format("MMM D, YYYY");
    return `${first} – ${last}`;
  }

  // ─── Table rows ──────────────────────────────────────────────────────────

  get tableRows() {
    return this.effectiveDays.map(d => {
      const actual = this.safeNum(d.actualPpd);
      const target = this.safeNum(d.targetPpd);
      const isOver =
        actual != null &&
        target != null &&
        actual >= target + 0.1 &&
        target > 0;
      return {
        label: moment(d.fullDate, "YYYY-MM-DD").format("ddd, MMM D"),
        isFuture: d.isFuture,
        actualPpd: d.actualPpd, // null for future empty days → table shows "—"
        schedPpd: d.schedPpd,
        targetPpd: d.targetPpd,
        isOver,
      };
    });
  }
}
